import { h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { useShopware } from '../shopware/ShopwareContext';
import { OrthomolIcon } from '~/components-common/OrthomolIcon';
import { formatPrice } from '../shopware/shopwareUtils';
import { getFreeShippingThreshold, refreshFreeShippingThreshold } from './freeShipping';

interface ShippingProgressBarProps {
  mode?: 'drawer' | 'page';
}

export const ShippingProgressBar = ({ mode = 'drawer' }: ShippingProgressBarProps) => {
  const { cart } = useShopware();

  // Free-shipping threshold from /pdp-data (ORT-3090), falling back to 50 EUR when no
  // /pdp-data value has been seen this session.
  const freeShippingAmount = getFreeShippingThreshold();

  const productItems = (cart?.lineItems || []).filter((item) => item.type === 'product');

  // Keep the free-shipping threshold in sync with the CURRENT (guest/customer) context whenever the
  // cart's product set appears or changes. This covers paths that skip both PDP load and login()
  // (already-logged-in navigation, the once-per-session login() short-circuit, a deep link straight
  // to /shop/cart), which otherwise leave a stale value in sessionStorage. Keyed on the product
  // numbers, not quantities, so qty edits don't refetch. (ORT-3109)
  const productSignature = productItems.map((item) => (item.payload as any)?.productNumber).join(',');
  const lastRefreshedSignature = useRef<string | null>(null);
  useEffect(() => {
    if (!productSignature) return;
    if (lastRefreshedSignature.current === productSignature) return;
    let cancelled = false;
    // Only remember the signature as refreshed once the /pdp-data call actually succeeds, so a
    // failed fetch retries when this product set is seen again instead of being skipped for the
    // rest of the session.
    refreshFreeShippingThreshold(cart?.lineItems).then((ok) => {
      if (!cancelled && ok) lastRefreshedSignature.current = productSignature;
    });
    return () => {
      cancelled = true;
    };
  }, [productSignature]);

  const nonPlanItems = productItems.filter((item) => (item.payload?.customFields as any)?.is_plan !== true);

  // Plan items always have free shipping — if the cart only contains plan items, skip the threshold logic
  const onlyPlanItems = productItems.length > 0 && nonPlanItems.length === 0;

  // For mixed carts, only count non-plan items towards the free shipping threshold
  const currentAmount = nonPlanItems.reduce((sum, item) => sum + (item.price?.totalPrice || 0), 0);

  const progressValue = onlyPlanItems ? freeShippingAmount : currentAmount;
  const remainingForFreeShipping = formatPrice(Math.max(0, freeShippingAmount - currentAmount));
  const hasReachedFreeShipping = onlyPlanItems || currentAmount >= freeShippingAmount;

  if (mode === 'page') {
    return (
      <div className="shopping-cart-free-shipping mb-4 mb-md-7 pb-3 text-center">
        <label className="d-flex flex-column gap-2">
          {hasReachedFreeShipping ? (
            <span className="d-flex align-items-center justify-content-center gap-2">
              <span className="text-primary mb-1">
                <OrthomolIcon name="check-24" />
              </span>
              <strong className="text-nowrap">Kostenloser Versand</strong>
            </span>
          ) : (
            <span>
              Nur noch {remainingForFreeShipping} &euro; und Du erhältst
              <strong className="text-nowrap"> kostenlosen Versand.</strong>
            </span>
          )}
          <progress
            id="free-shipping-progress-bar"
            value={progressValue}
            max={freeShippingAmount}
            style={{ '--background-color': 'white' } as any}
          >
            Momentaner Betrag: {formatPrice(currentAmount)} &euro;
          </progress>
        </label>
      </div>
    );
  }

  // drawer mode
  return (
    <div className="shopping-cart-free-shipping mb-4">
      <label className="d-block w-100 text-center small lh-base">
        {hasReachedFreeShipping ? (
          <strong className="text-nowrap">Kostenloser Versand</strong>
        ) : (
          <>
            Nur noch {remainingForFreeShipping} &euro; und Du erhältst{' '}
            <strong className="text-nowrap">kostenlosen Versand.</strong>
          </>
        )}
        <progress id="free-shipping-progress-bar" className="mt-2" value={progressValue} max={freeShippingAmount}>
          Momentaner Betrag: {formatPrice(currentAmount)} &euro;
        </progress>
      </label>
    </div>
  );
};
